import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Copy,
  CreditCard,
  History,
  Link2,
  Loader2,
  RefreshCw,
  Settings2,
  XCircle,
} from "lucide-react";
import { ProviderBrandLogo } from "@/components/ProviderBrandLogo";
import { ProviderCardsCarousel } from "@/components/ProviderCardsCarousel";
import { paymentProviderLogo } from "@/lib/provider-logos";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell, ClinicalSection } from "@/components/clinical";
import { WorkspaceHub, WorkspaceHubBack } from "@/components/workspace/WorkspaceHub";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { cn } from "@/lib/utils";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

const PAGOS_TABS = ["hub", "transacciones", "nuevo-cobro", "medios-de-cobro"] as const;
type PagosTab = (typeof PAGOS_TABS)[number];
const STATUS_FILTERS = ["all", "paid", "pending", "rejected", "recovery"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export const Route = createFileRoute("/app/pagos")({
  validateSearch: (s: Record<string, unknown>) => {
    const rawTab = typeof s.tab === "string" ? s.tab : "hub";
    const tab = PAGOS_TABS.includes(rawTab as PagosTab) ? (rawTab as PagosTab) : "hub";
    const rawStatus = typeof s.status === "string" ? s.status : "all";
    const status = STATUS_FILTERS.includes(rawStatus as StatusFilter) ? (rawStatus as StatusFilter) : "all";
    return { tab, status };
  },
  component: PagosPage,
  head: () => ({ meta: [{ title: "Pagos — Structura Market" }] }),
});

interface PaymentRequest {
  id: string;
  linkToken: string;
  amountCents: number;
  currency: string;
  description: string | null;
  status: string;
  origin: string;
  paywayAuthorizationCode: string | null;
  paywayCardLast4: string | null;
  paywayPaymentId: string | null;
  createdAt: string;
}

interface ProviderRow {
  id: string;
  label: string;
  lifecycle: string;
  entitled: boolean;
  planTierRequired: string;
  configurable: boolean;
  tenant: { enabled: boolean; configured: boolean; isDefault?: boolean } | null;
}

function fmt(cents: number, cur = "ARS") {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: cur }).format(cents / 100);
}

function statusBadge(s: string) {
  const map: Record<string, string> = {
    paid: "default",
    pending: "secondary",
    rejected: "destructive",
    expired: "outline",
    cancelled: "outline",
  };
  return (
    <Badge variant={(map[s] ?? "outline") as "default" | "secondary" | "destructive" | "outline"}>
      {s}
    </Badge>
  );
}

function lifecycleChip(lc: string) {
  const m: Record<string, { label: string; cls: string }> = {
    stable: { label: "Disponible", cls: "bg-emerald-500/15 text-emerald-400" },
    beta: { label: "Beta", cls: "bg-amber-500/15 text-amber-400" },
    upcoming: { label: "Próximamente", cls: "bg-slate-500/15 text-slate-400" },
    deprecated: { label: "Deprecado", cls: "bg-rose-500/15 text-rose-400" },
  };
  const x = m[lc] ?? m.upcoming;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${x.cls}`}>{x.label}</span>;
}

function providerCardStatus(p: ProviderRow, paywayOn: boolean) {
  const active = p.id === "payway" ? paywayOn : Boolean(p.tenant?.enabled && p.tenant?.configured);
  if (p.lifecycle === "deprecated") return { label: "Deprecado", active: false };
  if (!p.entitled) return { label: "Plan", active: false };
  if (p.lifecycle === "upcoming" || !p.configurable) return { label: "Próximamente", active: false };
  if (active) return { label: "Activo", active: true };
  if (p.id === "payway" ? paywayOn : p.tenant?.enabled) return { label: "Falta configurar", active: false };
  return { label: "Inactivo", active: false };
}

const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  all: "Todos los pagos",
  paid: "Procesados",
  pending: "Pendientes",
  rejected: "Rechazados",
  recovery: "A recuperar",
};

function matchesStatusFilter(r: PaymentRequest, status: StatusFilter): boolean {
  if (status === "all") return true;
  if (status === "recovery") return r.status === "pending" || r.status === "rejected";
  return r.status === status;
}

function PagosPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate({ from: "/app/pagos" });
  const { tab, status } = Route.useSearch();
  const [requests, setRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [paywayEnabled, setPaywayEnabled] = useState(false);
  const [dash, setDash] = useState<{
    processed: { n: number; cents: number };
    totals: Record<string, { n: number; cents?: number }>;
    recoveryAlerts: number;
    webhook?: { received: number; approved: number; rejected: number; duplicate: number; dunning: number };
    providers?: Array<{ provider: string; enabled: boolean; environment: string | null; configured: boolean }>;
    subscriptions?: Array<{ billingStatus: string; billingMode: string; count: number; nextCharge: string | null }>;
  } | null>(null);

  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState<{ url: string; qrSvg: string; providerLabel: string } | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [reqs, prov, cfg] = await Promise.all([
        apiGetJson<{ requests: PaymentRequest[] }>("/api/payments/requests", tid),
        apiGetJson<{ providers: ProviderRow[] }>("/api/payments/providers", tid),
        apiGetJson<{ config?: { paywayEnabled?: boolean } }>("/api/tenant/config", tid),
      ]);
      setRequests(reqs.requests);
      setProviders(prov.providers);
      setPaywayEnabled(Boolean(cfg.config?.paywayEnabled));
      apiGetJson<typeof dash>("/api/payments/dashboard", tid).then(setDash).catch(() => setDash(null));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleRequests = useMemo(
    () => requests.filter((r) => matchesStatusFilter(r, status)),
    [requests, status],
  );

  const goToTab = (nextTab: PagosTab, nextStatus: StatusFilter = "all") =>
    void navigate({ search: { tab: nextTab, status: nextStatus } });

  const visibleProviders = useMemo(
    () =>
      providers.filter(
        (p) =>
          p.lifecycle !== "deprecated" &&
          // API ya filtra globallyEnabled=false; defensa en cliente
          (p as ProviderRow & { globallyEnabled?: boolean }).globallyEnabled !== false,
      ),
    [providers],
  );

  const chargeableProviders = useMemo(() => {
    return visibleProviders.filter((p) => {
      if (!p.entitled || !p.configurable) return false;
      if (p.id === "payway") return paywayEnabled;
      return Boolean(p.tenant?.enabled && p.tenant?.configured);
    });
  }, [visibleProviders, paywayEnabled]);

  useEffect(() => {
    if (!chargeableProviders.length) {
      setSelectedProviderId("");
      return;
    }
    setSelectedProviderId((prev) =>
      prev && chargeableProviders.some((p) => p.id === prev) ? prev : chargeableProviders[0].id,
    );
  }, [chargeableProviders]);

  const selectedProvider = chargeableProviders.find((p) => p.id === selectedProviderId) ?? null;

  const createLink = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Monto inválido");
      return;
    }
    if (!selectedProviderId) {
      toast.error("Activá y configurá un medio de cobro en Configuración → Pagos");
      return;
    }
    setCreating(true);
    try {
      const res = await apiSendJson<{ url: string; qrSvg: string; providerLabel?: string }>(
        "/api/payments/requests",
        "POST",
        {
          amount: amt,
          description: desc || "Cobro",
          origin: "manual",
          providerId: selectedProviderId,
        },
        tid,
      );
      setLastLink({
        url: res.url,
        qrSvg: res.qrSvg,
        providerLabel: res.providerLabel ?? selectedProvider?.label ?? selectedProviderId,
      });
      setAmount("");
      setDesc("");
      await load();
      toast.success(`Link generado con ${res.providerLabel ?? selectedProvider?.label}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al generar link");
    } finally {
      setCreating(false);
    }
  };

  const copy = (url: string) => {
    void navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  if (tab === "hub") {
    return (
      <div>
        <DashboardHeader title="Pagos" subtitle="Cobrá con tarjeta: generá un link o QR de pago" />
        <ClinicalPageShell offset="none" className="pt-6">
          <MerchantExperienceBar screenId="pagos" />
          <WorkspaceHub
            title="Pagos"
            subtitle="Los cobros aparecen en Órdenes cuando un cliente paga. Elegí qué querés revisar o hacer."
            links={[
              {
                id: "transacciones",
                label: "Todos los pagos",
                description: dash ? `${dash.processed.n} cobros · ${fmt(dash.processed.cents)} procesado` : "Ver el historial completo",
                href: "/app/pagos",
                search: { tab: "transacciones", status: "all" },
                icon: History,
              },
              {
                id: "pendientes",
                label: "Pendientes",
                description: dash ? `${dash.totals.pending?.n ?? 0} esperando que el cliente pague` : "Cobros esperando pago",
                href: "/app/pagos",
                search: { tab: "transacciones", status: "pending" },
                icon: Loader2,
              },
              {
                id: "rechazados",
                label: "Rechazados",
                description: dash ? `${dash.totals.rejected?.n ?? 0} pagos rechazados` : "Cobros que no se pudieron procesar",
                href: "/app/pagos",
                search: { tab: "transacciones", status: "rejected" },
                icon: XCircle,
              },
              {
                id: "recuperar",
                label: "A recuperar",
                description: dash ? `${dash.recoveryAlerts} necesitan que hagas seguimiento` : "Pagos que conviene reintentar",
                href: "/app/pagos",
                search: { tab: "transacciones", status: "recovery" },
                icon: AlertTriangle,
              },
              {
                id: "nuevo-cobro",
                label: "Nuevo cobro",
                description: "Generá un link o QR de pago para tu cliente",
                href: "/app/pagos",
                search: { tab: "nuevo-cobro", status: "all" },
                icon: Link2,
              },
              {
                id: "medios-de-cobro",
                label: "Medios de cobro",
                description: `Cómo pueden pagarte tus clientes${dash?.providers?.length ? ` · ${dash.providers.filter((p) => p.enabled && p.configured).length} activos` : ""}`,
                href: "/app/pagos",
                search: { tab: "medios-de-cobro", status: "all" },
                icon: Settings2,
              },
            ]}
          />
          <ClinicalSection
            title="Actividad de pagos"
            description="Consultá el estado de tus cobros."
            className="mt-6"
          >
            <p className="text-sm text-muted-foreground">
              Los cobros aparecen en Órdenes cuando un cliente paga. Para conectar o cambiar un medio de pago, usá Configuración → Pagos.
            </p>
          </ClinicalSection>
        </ClinicalPageShell>
      </div>
    );
  }

  const pageTitle =
    tab === "nuevo-cobro" ? "Nuevo cobro" : tab === "medios-de-cobro" ? "Medios de cobro" : STATUS_FILTER_LABEL[status];

  return (
    <div>
      <DashboardHeader title={pageTitle} subtitle="Cobrá con tarjeta: generá un link o QR de pago" />
      <ClinicalPageShell offset="none" className="pt-6 space-y-6">
        <WorkspaceHubBack title="Pagos" onBack={() => goToTab("hub")} />

        {tab === "medios-de-cobro" ? (
          <>
            {dash?.providers && dash.providers.length > 0 ? (
              <ClinicalSection title="Estado de los medios de pago">
                <div className="flex flex-wrap gap-2">
                  {dash.providers.map((p) => (
                    <Badge key={p.provider} variant={p.enabled && p.configured ? "default" : "outline"}>
                      {(providers.find((x) => x.id === p.provider)?.label ?? p.provider)} ·{" "}
                      {p.enabled ? (p.configured ? (p.environment === "sandbox" ? "prueba" : p.environment === "production" ? "producción" : "activo") : "sin configurar") : "inactivo"}
                    </Badge>
                  ))}
                </div>
              </ClinicalSection>
            ) : null}

            <ClinicalSection
              title="Medios de cobro"
              description="Cómo pueden pagarte tus clientes, según tu plan. Editá la configuración en Configuración."
              actions={
                <Button size="sm" variant="outline" asChild>
                  <Link to="/app/settings" search={{ tab: "pagos" }}>
                    <Settings2 className="h-4 w-4" /> Configuración
                  </Link>
                </Button>
              }
            >
              {loading && providers.length === 0 ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
                </div>
              ) : (
                <ProviderCardsCarousel>
                  {visibleProviders.map((p) => {
                    const st = providerCardStatus(p, paywayEnabled);
                    return (
                      <div
                        key={p.id}
                        className={cn(
                          "flex min-w-[130px] shrink-0 flex-col items-center gap-2 rounded-xl border border-border bg-muted/50 p-3 text-center",
                          st.active && "border-primary/40 bg-primary/5",
                        )}
                      >
                        <ProviderBrandLogo
                          src={paymentProviderLogo(p.id)}
                          alt={p.label}
                          className="h-10 w-10"
                          fallback={<CreditCard className="h-4 w-4" />}
                        />
                        <div className="w-full space-y-1">
                          <p className="truncate text-xs font-semibold">{p.label}</p>
                          <div className="flex flex-wrap justify-center gap-1">{lifecycleChip(p.lifecycle)}</div>
                          <p className={cn("text-[10px]", st.active ? "text-emerald-400" : "text-muted-foreground")}>
                            {st.label}
                          </p>
                        </div>
                        {p.entitled && p.configurable ? (
                          <Button size="sm" variant="secondary" className="h-7 w-full text-[10px]" asChild>
                            <Link to="/app/settings" search={{ tab: "pagos" }}>
                              Configurar
                            </Link>
                          </Button>
                        ) : null}
                      </div>
                    );
                  })}
                </ProviderCardsCarousel>
              )}
            </ClinicalSection>
          </>
        ) : null}

        {tab === "nuevo-cobro" ? (
          <ClinicalSection
            title="Nuevo cobro"
            description={
              chargeableProviders.length
                ? "Elegí con qué medio cobrar y generá el link o QR para tu cliente."
                : "Activá y configurá al menos un medio de cobro en Configuración → Pagos."
            }
          >
            <div className="space-y-3">
              {chargeableProviders.length > 0 ? (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Cobrar con</p>
                    {chargeableProviders.length === 1 ? (
                      <p className="text-sm font-semibold">{chargeableProviders[0].label}</p>
                    ) : (
                      <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                        <SelectTrigger className="h-9 w-[220px] border-border bg-muted/50">
                          <SelectValue placeholder="Elegí cómo cobrar" />
                        </SelectTrigger>
                        <SelectContent>
                          {chargeableProviders.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <p className="pb-1 text-[11px] text-muted-foreground">
                    El cliente paga en el enlace de cobro usando {selectedProvider?.label ?? "el medio elegido"}.
                  </p>
                </div>
              ) : (
                <Button size="sm" variant="outline" asChild>
                  <Link to="/app/settings" search={{ tab: "pagos" }}>
                    Ir a configurar cobros
                  </Link>
                </Button>
              )}
              <div className="flex flex-wrap gap-2">
                <Input
                  type="number"
                  placeholder="Monto (ARS)"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="max-w-[160px] border-border bg-muted/50"
                  disabled={!chargeableProviders.length}
                />
                <Input
                  placeholder="Descripción"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  className="min-w-[200px] flex-1 border-border bg-muted/50"
                  disabled={!chargeableProviders.length}
                />
                <Button onClick={() => void createLink()} disabled={creating || !chargeableProviders.length}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  Generar link
                </Button>
              </div>
              {lastLink ? (
                <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-3 sm:flex-row sm:items-center">
                  <div className="h-32 w-32 shrink-0" dangerouslySetInnerHTML={{ __html: lastLink.qrSvg }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">
                      Link de pago · <span className="font-medium text-foreground">{lastLink.providerLabel}</span>
                    </p>
                    <p className="break-all text-sm font-medium">{lastLink.url}</p>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => copy(lastLink.url)}>
                        <Copy className="h-4 w-4" /> Copiar
                      </Button>
                      <a href={lastLink.url} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost">
                          Abrir
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </ClinicalSection>
        ) : null}

        {tab === "transacciones" ? (
          <ClinicalSection
            title={STATUS_FILTER_LABEL[status]}
            description="Cobros generados y su estado"
            actions={
              <Button size="sm" variant="ghost" onClick={() => void load()} aria-label="Recargar transacciones">
                <RefreshCw className="h-4 w-4" />
              </Button>
            }
          >
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : visibleRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {requests.length === 0 ? "Todavía no generaste cobros." : "No hay pagos en este estado."}
              </p>
            ) : (
              <div className="space-y-2">
                {visibleRequests.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card/60 p-3 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">
                        {fmt(r.amountCents, r.currency)} · {r.description ?? r.origin}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.createdAt).toLocaleString("es-AR")}
                        {r.origin.includes(":") ? ` · ${r.origin.split(":")[1]}` : ""}
                        {r.paywayCardLast4 ? ` · ****${r.paywayCardLast4}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(r.status)}
                      {r.status === "pending" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copy(`${window.location.origin}/pay/${r.linkToken}`)}
                        >
                          <Copy className="h-3 w-3" /> Link
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ClinicalSection>
        ) : null}
      </ClinicalPageShell>
    </div>
  );
}
